import { useMemo, useCallback, useEffect, useState } from 'react';
import { useLocation, navigate } from '@reach/router';
import { throttle } from 'utils';
import SearchParamsManager from 'utils/SearchParamsManager';
import { searchStore as store } from 'store';
import { SEARCH_SUGGESTIONS } from 'define/search';
import { isSsr } from 'define';

const filterSuggestions = search => {
  return SEARCH_SUGGESTIONS.filter(item =>
    item.toLowerCase().includes(search.toLowerCase().trim()),
  );
};

const shouldNavigateToSearch = currentPath => currentPath !== '/search';

const navigateToSearch = (input, location) => {
  navigate(`/search?q=${input}`, {
    state: { prevPath: location.pathname },
  });
};

const updateSearchParams = input => {
  SearchParamsManager.set({ q: input });
};

const shouldUpdateSearchFromQuery = (query, search, insideMenu) => {
  if (insideMenu) return false;
  return (query && !search) || query !== search;
};

export function useSearch({ insideMenu = false }) {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [showList, setShowList] = useState(false);

  const suggestions = useMemo(
    throttle(() => filterSuggestions(search), 300),
    [search],
  );

  const handleSearch = useCallback(e => {
    const hasValue = Boolean(e.target.value);
    setShowList(hasValue);
    setSearch(e.target.value);
  }, []);

  const handleInput = useCallback(
    input => {
      setShowList(false);
      const preparedInput = input.trim();
      setSearch(preparedInput);
      store.setSearch(preparedInput);

      if (shouldNavigateToSearch(location.pathname)) {
        navigateToSearch(preparedInput, location);
      } else {
        updateSearchParams(preparedInput);
      }
    },
    [location],
  );

  const handleKeyDown = useCallback(
    e => {
      const shouldSubmit = search.length > 0 && e.key === 'Enter';
      if (shouldSubmit) {
        handleInput(search);
      }
    },
    [search, handleInput],
  );

  const handleItemClick = useCallback(
    value => {
      handleInput(value);
    },
    [handleInput],
  );

  const clearSearch = useCallback(() => {
    setSearch('');
    setShowList(false);
  }, []);

  const query = !isSsr ? SearchParamsManager.get('q').toString() : '';

  useEffect(() => {
    if (shouldUpdateSearchFromQuery(query, search, insideMenu)) {
      setSearch(query);
      store.setSearch(query);
    }
  }, [query, insideMenu]);

  return {
    search,
    showList,
    handleSearch,
    handleKeyDown,
    handleInput,
    handleItemClick,
    clearSearch,
    suggestions,
  };
}
